import { firebaseContext } from 'components/firebaseDbRefContext'
import { child, push, update } from 'firebase/database'
import './sliderVariables.scss'
import { FC, ReactElement, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  storeSelectedMuniToLocalStorage,
  storeToLocalStorage,
} from '../../storeToLocalStorage'
import { unityPropsContext } from 'components/UnityPropsContext'
import type { Question } from 'Types/Questions'

interface Props {
  question: Question
  selectedBfsId?: number
  isMunicipality?: boolean
  guessedAnswer?: { average: number; count: number }
  unit: string
  min: number
  max: number
  step: number
  tenant: any
}
const numberFormatter = Intl.NumberFormat('de-CH')
const colorGuess = 'var(--purple-main)' // "#e93946"
const colorAnswer = 'var(--green-main)' // "#378EBD"
const localStorageItemName = 'swissQuizAnswers'

const AnsweringSlider: FC<Props> = ({
  question,
  selectedBfsId,
  isMunicipality = false,
  guessedAnswer,
  unit,
  min,
  max,
  step,
  tenant,
}): ReactElement => {
  const unityProps = useContext(unityPropsContext)
  const [rangeValue, setRangeValue] = useState(0)
  const [hasAnswered, setHasAnswered] = useState(false)
  const answerKey = isMunicipality ? 'municipality' : 'national'
  const noMunicipalityAnswer =
    selectedBfsId &&
    isMunicipality &&
    !question.answers.municipality[selectedBfsId]
  const uiDisabled =
    noMunicipalityAnswer || hasAnswered || (isMunicipality && !selectedBfsId)
  const firebaseGlobals = useContext(firebaseContext)
  const enoughGuessedAnswers =
    guessedAnswer?.average != null &&
    !isNaN(guessedAnswer!.average) &&
    +guessedAnswer?.count >= 5
  const answer: number = isMunicipality
    ? selectedBfsId
      ? question.answers['municipality'][selectedBfsId]
      : 0
    : question.answers['national']
  const isReversed =
    answer >
    (enoughGuessedAnswers ? guessedAnswer?.average || rangeValue : rangeValue)
  // insert the count of guesses before the last word inside the estimatedProportionLegend string
  const wordArray = unityProps.estimatedProportionLegend.trim().split(' ')
  wordArray.splice(
    wordArray.length - 1,
    0,
    String(numberFormatter.format(guessedAnswer?.count || 0))
  )
  const estimatedProportionLegendWithCount = wordArray.join(' ')
  useEffect(() => {
    let prevQuestionsString
    try {
      prevQuestionsString = localStorage.getItem(localStorageItemName)
    } catch {
      console.log('localStorage not available')
    }
    // reset range value if user switches municipality
    setHasAnswered(false)
    setRangeValue(min)

    if (prevQuestionsString) {
      const perviousAnsweredQuestions = JSON.parse(prevQuestionsString)
      if (
        (isMunicipality &&
          selectedBfsId &&
          perviousAnsweredQuestions[question.id]?.[answerKey]?.[
            selectedBfsId
          ] != null) ||
        (!isMunicipality &&
          perviousAnsweredQuestions[question.id]?.[answerKey] != null)
      ) {
        setHasAnswered(true)
        setRangeValue(
          isMunicipality && selectedBfsId
            ? +perviousAnsweredQuestions[question.id][answerKey][selectedBfsId]
            : +perviousAnsweredQuestions[question.id][answerKey]
        )
      }
    }
  }, [selectedBfsId])
  const submitUserAnswer = () => {
    if (!uiDisabled && firebaseGlobals.uniqueId) {
      storeToLocalStorage(
        isMunicipality,
        localStorageItemName,
        question,
        answerKey,
        rangeValue,
        selectedBfsId
      )
      selectedBfsId && storeSelectedMuniToLocalStorage(selectedBfsId)

      // write to answers
      update(
        child(firebaseGlobals.userAnswersRef, `${firebaseGlobals.uniqueId}/`),
        {
          ['answers/' +
          question.id +
          '/' +
          (isMunicipality ? 'municipality' : 'national')]: rangeValue,
          ...(selectedBfsId ? { bfsId: selectedBfsId } : ''),
        }
      )
    }
    setHasAnswered(true)
  }
  return (
    <Section hasAnswered={hasAnswered}>
      {noMunicipalityAnswer ? (
        <div>Aucune donnée disponible</div>
      ) : (
        <>
          {
            <LegendWrapper show={hasAnswered} isReversed={isReversed}>
              <LegendEntry color={colorAnswer} isRightSide={isReversed}>
                {unityProps.trueProportionLegend}
              </LegendEntry>
              <LegendEntry color={colorGuess} isRightSide={!isReversed}>
                {enoughGuessedAnswers ? estimatedProportionLegendWithCount : ''}
              </LegendEntry>{' '}
            </LegendWrapper>
          }
          <div style={{ display: 'table' }}>
            <div style={{ display: 'table-cell' }}>
              <ResultsWrapper>
                <input
                  step={step}
                  type='range'
                  min={min}
                  max={max}
                  className='slider'
                  onChange={(e: any) => {
                    if (!hasAnswered) setRangeValue(+e.target.value)
                  }}
                  value={rangeValue}
                  disabled={uiDisabled}
                  id={question.id + (isMunicipality ? '-gemeinde' : '')}
                ></input>
                <CurrentValue
                  percentage={((rangeValue - min) / (max - min)) * 100}
                  hasAnswered={hasAnswered}
                  className={hasAnswered ? 'hasAnswered' : ''}
                >
                  {rangeValue.toLocaleString(tenant.lang + '-CH')} {unit}
                </CurrentValue>

                {hasAnswered && (
                  <UserAnswer
                    percentage={((rangeValue - min) / (max - min)) * 100}
                  >
                    <svg
                      width='2'
                      height='18'
                      viewBox='0 0 2 18'
                      xmlns='http://www.w3.org/2000/svg'
                      id='arrow-icon'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <line
                        x1='1'
                        y1='1'
                        x2='1'
                        y2='17'
                        stroke='currentColor'
                      />
                    </svg>
                    <svg
                      width='0.75em'
                      height='0.75em'
                      viewBox='0 0 13 14'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      id='user-icon'
                    >
                      <path d='M6.25 7.5C8.04493 7.5 9.5 6.04493 9.5 4.25C9.5 2.45507 8.04493 1 6.25 1C4.45507 1 3 2.45507 3 4.25C3 6.04493 4.45507 7.5 6.25 7.5Z' />
                      <path d='M1 12.5C1 10 3 7.5 6.25 7.5C9.5 7.5 11.5 10 11.5 12.5' />
                    </svg>
                  </UserAnswer>
                )}

                {/* GUESS OF OUR READERS */}
                {enoughGuessedAnswers && !isNaN(answer) && (
                  <SliderResult
                    hide={!hasAnswered}
                    isReversed={!isReversed}
                    transition
                    color={colorGuess}
                    percentage={
                      hasAnswered
                        ? ((Number(guessedAnswer.average.toFixed(1)) - min) /
                            (max - min)) *
                          100
                        : !isReversed
                        ? max
                        : min
                    }
                  >
                    <ResultLabel>
                      {Math.round(guessedAnswer.average).toLocaleString(
                        tenant.lang + '-CH'
                      )}{' '}
                      {unit}
                    </ResultLabel>
                    <Dot />
                  </SliderResult>
                )}

                {/* THE CORRECT ANSWER */}
                <SliderResult
                  hide={!hasAnswered}
                  isReversed={isReversed}
                  transition
                  color={colorAnswer}
                  percentage={
                    hasAnswered
                      ? ((answer - min) / (max - min)) * 100
                      : isReversed
                      ? max
                      : min
                  }
                >
                  <ResultLabel>
                    {answer.toLocaleString(tenant.lang + '-CH')} {unit}
                  </ResultLabel>
                  <Dot />
                </SliderResult>
              </ResultsWrapper>
            </div>
          </div>

          {hasAnswered && (
            <AnswerDescription>
              {question.answers.description}
            </AnswerDescription>
          )}

          <Button onClick={submitUserAnswer} disabled={uiDisabled}>
            {unityProps.submit}
          </Button>
        </>
      )}
    </Section>
  )
}
const OnRange = styled.div.attrs<{
  percentage?: number
}>((props) => ({
  style: {
    left:
      typeof props.percentage !== 'undefined'
        ? 'calc((' +
          props.percentage / 100 +
          ' * ((100% - var(--half-thumb-size)) - var(--half-thumb-size))) + var(--half-thumb-size))'
        : 0,
  },
}))`
  position: absolute;
  top: 50%;
`

const AnswerDescription = styled.div`
  margin-top: 2em;
  font-size: 0.75em;
  line-height: 1.4em;
  background: var(--info-box-background);
  color: var(--text-color);
  padding: 15px;
  border-radius: 5px;

  @media screen and (max-width: 600px) {
    font-size: 18px;
  }

  @media screen and (max-width: 350px) {
    font-size: 16px;
  }
`

const Section = styled.section<{ hasAnswered: boolean }>`
  font-size: 21px;
  @media screen and (max-width: 600px) {
    font-size: 18px;
  }
  @media screen and (max-width: 350px) {
    font-size: 16px;
  }
  --thumb-size: 25px;
  --half-thumb-size: calc(var(--thumb-size) / 2);
  --dot-size: 0.75em;
  --half-dot-size: calc(var(--dot-size) / 2);

  display: flex;
  flex-direction: column;
  /* max-width: 40rem; */
  input[type='range'] {
    &::-webkit-slider-thumb {
      ${(props) => (props.hasAnswered ? 'display: none;' : '')}
      cursor: ew-resize;
    }
    &::-moz-range-thumb {
      ${(props) => (props.hasAnswered ? 'display: none; opacity:0;' : '')}
      cursor: ew-resize;
    }
    &::-ms-thumb {
      ${(props) => (props.hasAnswered ? 'display: none;' : '')}
      cursor: ew-resize;
    }
  }

  input {
    position: absolute;

    top: 50%;
    z-index: 0;
    transform: translateY(-50%);
    margin: 0;
  }
`
const Button = styled.button`
  &:disabled {
    opacity: 0;
    max-height: 0;
  }
  cursor: pointer;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 1em;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px;
  gap: 10px;
  max-height: 100px;
  transition: all 250ms cubic-bezier(0.77, 0, 0.175, 1);
  width: 133px;
  height: 35px;

  /* --brandblue-main */
  outline: none;
  border: none;
  background: var(--ui-background-color);
  color: var(--site-background);
  font-weight: 700;
`
const Dot = styled.span`
  /* transform: translateX(-50%); */
  display: inline-block;
  border-radius: 50%;
  width: var(--dot-size);
  height: var(--dot-size);
`
const SliderResult = styled(OnRange)<{
  hide?: boolean
  isReversed?: boolean
  percentage?: number
  color?: string
  transition?: boolean
}>`
  transition: ${(props) =>
    props.transition ? 'all 500ms cubic-bezier(0.77, 0, 0.175, 1)' : ''};
  transform: ${(props) =>
      props.isReversed
        ? 'translateX(calc(-1* var(--half-dot-size)))'
        : 'translateX(calc(-100% + var(--half-dot-size)))'}
    translateY(-50%);
  display: flex;
  opacity: ${(props) => (props.hide ? '0' : '1')};
  pointer-events: none;
  flex-direction: ${(props) => (props.isReversed ? 'row-reverse' : 'row')};
  align-items: center;
  justify-content: center;
  color: ${(props) => props.color || 'var(--brandblue-main);'};
  span {
    background-color: ${(props) => props.color || '#D3343F'};
  }
`
const ResultLabel = styled.label`
  /* background-color:  var(--site-background); */
  background-color: var(--result-label-background);
  /* backdrop-filter: blur(0.5px); */
  border-radius: 4px;
  font-weight: 1000;
  padding: 1px 2px;
  font-size: 0.7em;
`
const ResultsWrapper = styled.div`
  position: relative;
  height: 4em;
  margin: auto 2em;
`
const LegendWrapper = styled.div<{ isReversed?: boolean; show?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: ${(props) => (props.show ? '1' : '0')};
  max-height: ${(props) => (props.show ? '100px' : '1px')};
  transition: all 500ms cubic-bezier(0.77, 0, 0.175, 1);
  flex-direction: ${(props) => (props.isReversed ? 'row-reverse' : 'row')};
`
const LegendEntry = styled.div<{ color: string; isRightSide?: boolean }>`
  color: ${(props) => props.color};
  font-weight: bold;
  ${(props) => props.isRightSide && 'text-align: right;'}
  font-size: 0.7em;
`
const PersonArray = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  justify-items: center;
`
const CurrentValue = styled(OnRange)<{
  percentage?: number
  hasAnswered?: boolean
}>`
  font-size: 0.7em;
  &.hasAnswered {
    transition: transform 500ms cubic-bezier(0.77, 0, 0.175, 1);
    @media screen and (max-width: 600px) {
      transform: translateY(2.5em) translateX(-50%);
    }
    transform: translateY(2.3em) translateX(-50%);
  }
  transform: translateY(-2.5em) translateX(-50%);
  font-weight: 700;
`
const UserAnswer = styled(OnRange)<{
  percentage?: number
}>`
  /* font-size: 0.7em; */
  transform: translateY(-0.43em) translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 0.3em;
  z-index: 3;
  stroke-width: 2;
  #user-icon {
    stroke: var(--text-color);
  }
  #arrow-icon {
    fill: var(--text-color);
    opacity: 0.6;
  }
`
export default AnsweringSlider
